import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';

interface CertificateData {
  cert_number: string;
  issued_at: string;
  users: {
    name: string;
    company: string;
  };
}

const CertificateVerificationPage: React.FC = () => {
  const { certNumber } = useParams<{ certNumber: string }>();
  const [certificate, setCertificate] = useState<CertificateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      if (!certNumber) {
        setError('Keine Zertifikatsnummer angegeben');
        setLoading(false);
        return;
      }

      try {
        // Call the verify-certificate Edge Function
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-certificate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            certNumber: certNumber,
            userAgent: navigator.userAgent,
            verificationSource: 'web'
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Certificate verification error:', errorData);
          setError(errorData.error || 'Fehler beim Überprüfen des Zertifikats');
          return;
        }

        const result = await response.json();
        
        if (!result.valid) {
          setError(result.error || 'Zertifikat nicht gefunden');
          return;
        }

        // Certificate is valid - set the certificate data
        setCertificate(result.certificate);
      } catch (err) {
        console.error('Verification error:', err);
        setError('Fehler beim Überprüfen des Zertifikats');
      } finally {
        setLoading(false);
      }
    };

    verifyCertificate();
  }, [certNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Zertifikat wird überprüft...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <Link 
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Zurück zur Startseite
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Zertifikatsverifikation
            </h1>

            {error ? (
              <div className="text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-red-600 mb-4">
                  Zertifikat ungültig
                </h2>
                <p className="text-gray-600 mb-6">
                  {error}
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium mb-1">Mögliche Gründe:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Die Zertifikatsnummer ist falsch oder wurde falsch eingegeben</li>
                        <li>Das Zertifikat wurde widerrufen oder ist abgelaufen</li>
                        <li>Das Zertifikat wurde nicht von KI-Navigatoren ausgestellt</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : certificate ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-green-600 mb-6">
                  Zertifikat gültig
                </h2>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Zertifikatsnummer</p>
                      <p className="font-mono text-gray-800">{certificate.cert_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Ausstellungsdatum</p>
                      <p className="text-gray-800">
                        {new Date(certificate.issued_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Teilnehmer</p>
                      <p className="text-gray-800 font-medium">{certificate.users.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Unternehmen</p>
                      <p className="text-gray-800">{certificate.users.company}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-blue-700 text-left">
                      <p className="font-medium mb-1">Über dieses Zertifikat:</p>
                      <p>
                        Dieses Zertifikat bestätigt die erfolgreiche Teilnahme am 
                        KI-Kompetenznachweis nach EU AI Act der KI-Navigatoren GmbH. 
                        Der Teilnehmer hat alle erforderlichen Module absolviert und 
                        den Abschlusstest mit mindestens 75% bestanden.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                Ausgestellt von <strong>KI-Navigatoren GmbH</strong>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Diese Verifikation wurde am {new Date().toLocaleDateString('de-DE')} um {new Date().toLocaleTimeString('de-DE')} durchgeführt
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerificationPage;